import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';
import { queryRoots } from '../../../../lib/events';

/**
 * Organization tree and PKV defaults (docs/dev/api-organization.md „Organization tree", „PKV settings"; PKV M0).
 * Both keys live under the root `['organization']`, which `organization.updated` (sections `tree`, `pkv`, `modules`)
 * refreshes. Every tree change can add or end servicing memberships, so it refreshes the users table as well.
 */

export type BillingPayer = 'self' | 'parent';

export interface TreeOrganization {
  id: string;
  name: string;
  slug: string;
}

export interface TreeChild extends TreeOrganization {
  billingPayer: BillingPayer;
  /** „Selbstzahler" | „Gebühren trägt <Org>". */
  billingPayerLabel: string;
  memberCount: number;
  /** Keys of the child's active modules (`organizationModuleKeys`). */
  modules: string[];
}

export interface OrganizationTree {
  organization: TreeOrganization;
  parent: TreeOrganization | null;
  billingPayer: BillingPayer;
  /** The own organization's payer: „Selbstzahler" | „Gebühren trägt <Eltern-Org>". */
  billingPayerLabel: string;
  children: TreeChild[];
  ancestors: TreeOrganization[];
}

export interface PkvSettings {
  pkvCalculationDailyLimit: number;
  pkvDefaultMbFactor: number | null;
  pkvDefaultRetentionPct: number | null;
  pkvFunnelEnabled: boolean;
  rvaFourEyesAnonymization: boolean;
  rvaFourEyesRelease: boolean;
  rvaInsurerDeadlineDays: number;
}

export type PkvSettingsPatch = Partial<PkvSettings>;

export const TREE_PATH = '/settings/organization/tree';
export const PKV_SETTINGS_PATH = '/settings/organization/pkv';

export const treeKeys = {
  tree: [...queryRoots.organization, 'tree'] as const,
  pkv: [...queryRoots.organization, 'pkv'] as const,
};

/** The requests of the page, kept apart from the hooks so the bodies are testable. */
export const treeRequests = {
  tree: () => api<OrganizationTree>(TREE_PATH),
  addChild: (organizationSlug: string) =>
    api<OrganizationTree>(`${TREE_PATH}/children`, { method: 'POST', body: { organizationSlug } }),
  removeChild: (id: string) => api<OrganizationTree>(`${TREE_PATH}/children/${id}`, { method: 'DELETE' }),
  setBillingPayer: (id: string, billingPayer: BillingPayer) =>
    api<OrganizationTree>(`${TREE_PATH}/children/${id}/billing-payer`, {
      method: 'PUT',
      body: { billingPayer },
    }),
  pkvSettings: () => api<PkvSettings>(PKV_SETTINGS_PATH),
  patchPkvSettings: (patch: PkvSettingsPatch) =>
    api<PkvSettings>(PKV_SETTINGS_PATH, { method: 'PATCH', body: patch }),
};

export function useOrganizationTree() {
  return useQuery({ queryKey: treeKeys.tree, queryFn: treeRequests.tree, retry: false });
}

export function usePkvSettings(enabled: boolean) {
  return useQuery({ queryKey: treeKeys.pkv, queryFn: treeRequests.pkvSettings, retry: false, enabled });
}

/** Applies the server's tree and refreshes the users (servicing memberships follow every tree change). */
function useApplyTree() {
  const queryClient = useQueryClient();
  return (tree: OrganizationTree) => {
    queryClient.setQueryData(treeKeys.tree, tree);
    void queryClient.invalidateQueries({ queryKey: queryRoots.users });
  };
}

export function useAddChild() {
  const apply = useApplyTree();
  return useMutation({ mutationFn: treeRequests.addChild, onSuccess: apply });
}

export function useRemoveChild() {
  const apply = useApplyTree();
  return useMutation({ mutationFn: treeRequests.removeChild, onSuccess: apply });
}

export function useSetBillingPayer() {
  const apply = useApplyTree();
  return useMutation({
    mutationFn: ({ id, billingPayer }: { id: string; billingPayer: BillingPayer }) =>
      treeRequests.setBillingPayer(id, billingPayer),
    onSuccess: apply,
  });
}

export function useSavePkvSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: treeRequests.patchPkvSettings,
    onSuccess: (settings) => queryClient.setQueryData(treeKeys.pkv, settings),
  });
}
